import React, { useMemo } from 'react';
import { Shield, Gem, FileText, Paperclip } from 'lucide-react';

const TYP_ICON = { pov: Shield, hav: Gem, gap: FileText, jine: FileText };
const TYP_LABEL = { pov: 'POV', hav: 'HAV', gap: 'GAP', jine: 'Jiné' };

const STATUS_STYLES = {
    green:  { color: '#10B981', bg: 'rgba(16,185,129,0.12)',  border: 'rgba(16,185,129,0.30)' },
    yellow: { color: '#FFC857', bg: 'rgba(255,200,87,0.12)',  border: 'rgba(255,200,87,0.30)' },
    red:    { color: '#EF4444', bg: 'rgba(239,68,68,0.12)',   border: 'rgba(239,68,68,0.30)' },
    gray:   { color: '#9CA3AF', bg: 'rgba(156,163,175,0.12)', border: 'rgba(156,163,175,0.30)' },
};

function dniDoExpirace(dateStr) {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

function statusSmlouvy(dni) {
    if (dni === null) return { key: 'gray', label: 'Bez data' };
    if (dni < 0) return { key: 'red', label: 'Po splatnosti' };
    if (dni <= 60) return { key: 'yellow', label: `Končí za ${dni} dní` };
    return { key: 'green', label: 'Aktivní' };
}

function Badge({ status, isDark }) {
    const s = STATUS_STYLES[status.key];
    return (
        <span style={{
            display: 'inline-flex',
            padding: '3px 8px',
            borderRadius: 5,
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            background: s.bg,
            color: s.color,
            border: `1px solid ${s.border}`,
            whiteSpace: 'nowrap',
        }}>
            {status.label}
        </span>
    );
}

export default function PojistniKarta({ vozidlo, smlouvy = [], onClick, cardStyle, isDark }) {
    const labelColor = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const subtle = isDark ? 'rgba(255,255,255,0.65)' : 'var(--text-secondary)';
    const textColor = isDark ? '#fff' : 'var(--text-primary)';
    const policyBg = isDark ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.025)';
    const policyBorder = isDark ? 'rgba(255,255,255,0.05)' : 'var(--border-default)';

    const aggregate = useMemo(() => {
        if (smlouvy.length === 0) return { key: 'gray', label: 'Bez smluv' };
        const order = { red: 3, yellow: 2, green: 1, gray: 0 };
        let worst = 'green';
        for (const s of smlouvy) {
            const dni = dniDoExpirace(s.platnost_do);
            const st = statusSmlouvy(dni);
            if (order[st.key] > order[worst]) worst = st.key;
        }
        if (worst === 'red') return { key: 'red', label: 'Po splatnosti' };
        if (worst === 'yellow') return { key: 'yellow', label: 'Končí brzy' };
        return { key: 'green', label: 'Vše OK' };
    }, [smlouvy]);

    const pojistki = smlouvy.filter((s) => s.typ === 'pojisteni' || !s.typ);
    const soubory = smlouvy.filter((s) => s.soubor_cesta);

    return (
        <button
            type="button"
            onClick={onClick}
            style={{
                ...cardStyle,
                borderRadius: 10,
                padding: 18,
                cursor: 'pointer',
                textAlign: 'left',
                width: '100%',
                color: textColor,
                transition: 'transform 0.15s, border-color 0.15s, box-shadow 0.15s',
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = '';
            }}
        >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                <div style={{ minWidth: 0 }}>
                    <div style={{
                        fontWeight: 800, fontSize: 16, letterSpacing: 0.5,
                        fontVariantNumeric: 'tabular-nums', color: textColor,
                    }}>
                        {vozidlo.spz}
                    </div>
                    <div style={{ fontSize: 12, color: labelColor, marginTop: 2 }}>
                        {vozidlo.model}{vozidlo.rok_vyroby ? ` · ${vozidlo.rok_vyroby}` : ''}
                    </div>
                </div>
                <Badge status={aggregate} isDark={isDark} />
            </div>

            {pojistki.length === 0 && (
                <div style={{ fontSize: 12, color: labelColor, padding: '12px 0', textAlign: 'center' }}>
                    Žádná pojistná smlouva
                </div>
            )}

            {pojistki.map((s) => {
                const Icon = TYP_ICON[s.pojisteni_typ] || Shield;
                const dni = dniDoExpirace(s.platnost_do);
                const status = statusSmlouvy(dni);
                const datumColor = status.key === 'red' ? '#EF4444'
                    : status.key === 'yellow' ? '#FFC857'
                    : labelColor;
                return (
                    <div key={s.id} style={{
                        background: policyBg,
                        borderRadius: 7,
                        padding: 12,
                        marginTop: 10,
                        border: `1px solid ${policyBorder}`,
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <Icon size={14} style={{ color: '#0CC0DF' }} />
                            <span style={{ fontSize: 12, fontWeight: 700, color: textColor }}>
                                {TYP_LABEL[s.pojisteni_typ] || s.pojisteni_typ?.toUpperCase() || '—'}
                                {s.pojistovna ? ` — ${s.pojistovna}` : ''}
                            </span>
                            <span style={{ marginLeft: 'auto' }}>
                                <Badge status={status} isDark={isDark} />
                            </span>
                        </div>
                        <div style={{ fontSize: 11, color: subtle, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                            {s.cislo_smlouvy && (
                                <span style={{ fontVariantNumeric: 'tabular-nums' }}>č. {s.cislo_smlouvy}</span>
                            )}
                            {s.platnost_do && (
                                <span style={{ fontVariantNumeric: 'tabular-nums', color: datumColor }}>
                                    do {new Date(s.platnost_do).toLocaleDateString('cs-CZ')}
                                </span>
                            )}
                            {s.rocni_pojistne_kc && (
                                <span style={{
                                    fontVariantNumeric: 'tabular-nums', fontWeight: 700,
                                    marginLeft: 'auto', color: '#FF8A00',
                                }}>
                                    {Number(s.rocni_pojistne_kc).toLocaleString('cs-CZ')} Kč
                                </span>
                            )}
                        </div>
                    </div>
                );
            })}

            {soubory.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
                    {soubory.map((s) => (
                        <span key={s.id} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '3px 8px', borderRadius: 5,
                            fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
                            letterSpacing: 0.4,
                            background: 'rgba(156,163,175,0.12)',
                            color: subtle,
                            border: '1px solid rgba(156,163,175,0.25)',
                        }}>
                            <Paperclip size={10} />
                            {s.soubor_nazev || (s.typ === 'zelena_karta' ? 'zelená karta' : 'smlouva.pdf')}
                        </span>
                    ))}
                </div>
            )}
        </button>
    );
}
